import { html, css } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { booleanConverter } from '../common/converters';
import { TeComponent } from '../common/te-component';
import { theme } from '../common/theme';
import './../te-spacing';
import { TeTable } from './te-table';

/**
 * Table Row Component
 *
 * @slot - Default slot for rendering table cells
 */
@customElement('te-table-row')
export class TeTableRow extends TeComponent {
  static styles = [
    ...TeComponent.styles,
    css`
      .te-table-row {
        display: flex;
        justify-content: space-between;
        flex: 1 0;
      }

      .te-table-column {
        display: flex;
        justify-content: space-between;
        flex-direction: column;
        flex: 0 1;
      }

      .te-table-row ::slotted(*) {
        flex: 1;
      }

      .te-table-row ::slotted(*:not(:last-child)) {
        border-right: 1px solid ${theme.colors.shade2};
      }

      .te-table-column ::slotted(*) {
        flex: 1;
        border-right: 1px solid ${theme.colors.shade2};
      }

      .te-table-row:not(.last) ::slotted(*) {
        border-bottom: 1px solid ${theme.colors.shade2};
      }

      .te-table-column ::slotted(*:not(:last-child)) {
        border-bottom: 1px solid ${theme.colors.shade2};
      }
    `,
  ];

  @state() rowIndex = 0;
  @state() scrollbarOffset = 0;
  @state() spacingDirection: 'x' | 'y' = 'x';
  @state() containerClass: 'te-table-row' | 'te-table-column' = 'te-table-row';

  @query('slot') slotContent?: HTMLSlotElement;

  /** Row is selected and active */
  @property({ type: Boolean, converter: booleanConverter }) active = false;

  getRowIndex() {
    if (!this.parentElement || this.containerClass === 'te-table-column') return 0;
    const table = this.parentElement;
    const nodes = [...table.childNodes].filter((node) => node.nodeName === this.nodeName);
    return nodes.findIndex((node) => node === this);
  }

  firstUpdated() {
    this.rowIndex = this.getRowIndex();
  }

  updated() {
    const table = this.parentElement as TeTable;
    table.setRowSelected(this.rowIndex, this.active);
  }

  render() {
    const isLast =
      !!this.parentElement && this.containerClass !== 'te-table-column' && !this.nextElementSibling;
    const classes = [this.containerClass, isLast && 'last'].filter(Boolean);
    return html`
      <div class="${classes.join(' ')}" @click=${this.handleClick}>
        <slot></slot>
        <te-spacing direction="${this.spacingDirection}" size=${this.scrollbarOffset}></te-spacing>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-table-row': TeTableRow;
  }
}
